// Resilient Fetch Utility
// Provides retry logic with exponential backoff and timeout handling

export class FetchError extends Error {
  constructor(
    message: string,
    public status?: number,
    public isTimeout: boolean = false,
    public isNetworkError: boolean = false
  ) {
    super(message);
    this.name = 'FetchError';
  }
}

export interface ResilientFetchOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

// Timeout configuration for different operation types
export const TIMEOUTS = {
  DEFAULT: 10000,        // 10 seconds - standard API calls
  HEALTH_CHECK: 5000,    // 5 seconds - health checks
  HEAVY_OPERATION: 30000, // 30 seconds - heavy operations (match, fix)
} as const;

const DEFAULT_TIMEOUT = TIMEOUTS.DEFAULT;
const DEFAULT_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 1000; // 1 second - will be multiplied exponentially

// HTTP status codes that should NOT be retried (client errors)
const NON_RETRYABLE_STATUS_CODES = [400, 401, 403, 404, 422];

// HTTP status codes that SHOULD be retried (server errors)
const RETRYABLE_STATUS_CODES = [500, 502, 503, 504];

/**
 * Sleeps for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Determines if an error is retryable
 */
function isRetryableError(error: Error | FetchError): boolean {
  if (error instanceof FetchError) {
    // Always retry on timeout and network errors
    if (error.isTimeout || error.isNetworkError) return true;

    // Check against explicit retry/non-retry lists
    if (error.status) {
      // Don't retry client errors
      if (NON_RETRYABLE_STATUS_CODES.includes(error.status)) return false;
      // Retry server errors
      if (RETRYABLE_STATUS_CODES.includes(error.status)) return true;
      // Retry other 5xx errors not in explicit list
      if (error.status >= 500 && error.status < 600) return true;
      // Don't retry other status codes
      return false;
    }
  }
  return true;
}

/**
 * Calculates exponential backoff delay
 */
function getBackoffDelay(attempt: number, baseDelay: number): number {
  return baseDelay * Math.pow(2, attempt - 1);
}

/**
 * Fetch with timeout support
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit & { timeout?: number }
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new FetchError('Request timeout', undefined, true, false);
      }
      // Network errors (no internet, DNS failure, etc.)
      if (error.message.includes('fetch') || error.message.includes('network')) {
        throw new FetchError('Network error', undefined, false, true);
      }
    }
    throw error;
  }
}

/**
 * Resilient fetch with retry logic and exponential backoff
 */
export async function resilientFetch(
  url: string,
  options: ResilientFetchOptions = {}
): Promise<Response> {
  const {
    timeout = DEFAULT_TIMEOUT,
    retries = DEFAULT_RETRIES,
    retryDelay = DEFAULT_RETRY_DELAY,
    onRetry,
    ...fetchOptions
  } = options;

  let lastError: Error | FetchError | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, {
        ...fetchOptions,
        timeout,
      });

      // Check if response is OK
      if (!response.ok) {
        const error = new FetchError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          false,
          false
        );

        // Only retry if it's a retryable error and we have attempts left
        if (!isRetryableError(error) || attempt === retries) {
          throw error;
        }

        lastError = error;
      } else {
        return response;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // If it's the last attempt or error is not retryable, throw
      if (attempt === retries || !isRetryableError(lastError)) {
        throw lastError;
      }
    }

    // Calculate backoff delay and wait before retrying
    const delay = getBackoffDelay(attempt + 1, retryDelay);

    if (onRetry && lastError) {
      onRetry(attempt + 1, lastError);
    }

    await sleep(delay);
  }

  // Should never reach here, but just in case
  throw lastError || new Error('Unknown error occurred');
}

/**
 * Resilient JSON fetch - combines resilient fetch with JSON parsing
 */
export async function resilientFetchJSON<T>(
  url: string,
  options: ResilientFetchOptions = {}
): Promise<T> {
  const response = await resilientFetch(url, options);

  try {
    const data = await response.json();
    return data as T;
  } catch {
    throw new FetchError(
      'Failed to parse JSON response',
      response.status,
      false,
      false
    );
  }
}

/**
 * Check if the browser is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Wait for the browser to come back online
 */
export function waitForOnline(): Promise<void> {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve();
      return;
    }

    const handleOnline = () => {
      window.removeEventListener('online', handleOnline);
      resolve();
    };

    window.addEventListener('online', handleOnline);
  });
}

/**
 * Get a user-friendly error message from a FetchError
 */
export function getFriendlyErrorMessage(error: Error | FetchError): string {
  if (error instanceof FetchError) {
    if (error.isTimeout) {
      return 'Request timed out. Please check your connection and try again.';
    }
    if (error.isNetworkError) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    if (error.status === 404) {
      return 'The requested resource was not found.';
    }
    if (error.status === 401 || error.status === 403) {
      return 'Authentication required. Please log in and try again.';
    }
    if (error.status && error.status >= 500) {
      return 'Server error. Please try again later.';
    }
  }
  return error.message || 'An unexpected error occurred';
}

/**
 * Check if an error is a timeout error
 */
export function isTimeoutError(error: Error | FetchError): boolean {
  return error instanceof FetchError && error.isTimeout;
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: Error | FetchError): boolean {
  return error instanceof FetchError && error.isNetworkError;
}
